import { useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { forgotPassword } from '@/api/user.api';
import { useToast } from '@/contexts/toast-context';

import { Card, CardContent, Button, Label, Input } from '@/components/ui';
import { Spinner, AuthFooterLinks } from '../../components';

import './forgot-password-style.scss';
import { TOAST_TYPES } from '@/constants/common.constants';

export const Route = createFileRoute('/forgot-password/')({
  component: ForgotPassword,
});

function ForgotPassword() {
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | undefined>();

  const handleSubmit = async () => {
    // Validate email
    if (!email) {
      setError('Email address is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setError(undefined);

    try {
      setIsSubmitting(true);
      await forgotPassword(email);
      setIsSubmitted(true);
    } catch (err: unknown) {
      addToast('Failed to send reset link', { type: TOAST_TYPES.ERROR });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate({ to: '/login' });
  };

  return (
    <div className="forgot-password-container">
      <Card className="forgot-password-card">
        <CardContent>
          <div className="forgot-password-header">
            <img
              src="/full-logo.svg"
              alt="Logo"
              className="forgot-password-logo"
            />
            <h1>Reset Your Password</h1>
          </div>

          {isSubmitted ? (
            <div className="forgot-password-success">
              <p>
                We've sent a password reset link to the contact method
                associated with this request for
                <strong> {email}</strong>.
              </p>
              <p>Please check your messages and follow the instructions.</p>
              <p className="forgot-password-note">
                If you don't receive it, check your spam folder or try again
                later.
              </p>
              <Button
                variant="basic-primary"
                className="forgot-password-back-button"
                size="medium"
                onClick={handleCancel}
              >
                Back to Login
              </Button>
            </div>
          ) : (
            <>
              <p className="forgot-password-description">
                Enter your email address and we'll send you a link to reset your
                password.
              </p>

              <div className="forgot-password-field">
                <Label>Email address</Label>
                <Input
                  value={email}
                  type="email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) {
                      setError(undefined);
                    }
                  }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmit();
                    }
                  }}
                />
                {error && <p className="invalid-feedback">{error}</p>}
              </div>

              <div className="forgot-password-actions">
                <Button
                  variant="outline-primary"
                  size="medium"
                  onClick={handleCancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="basic-primary"
                  size="medium"
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                >
                  {isSubmitting && <Spinner size="small" />}
                  Send Reset Link
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
      <AuthFooterLinks />
    </div>
  );
}
